import { CloudUpload } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import List from "@mui/material/List";
import React, { useState, useMemo, useCallback } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { ApiResponse } from "src/apis";
import CustomLoader from "src/shared_components/CustomLoader";
import PermissionRestrictedComponent from "src/shared_components/PermissionRestrictedComponent";
import { useSnackbar } from "src/context/ShowMessage";
import Document from "src/assets/files/document.svg";

interface FileUploadDialogProps {
  title: string;
  fieldId: number;
  open: boolean;
  onClose: () => void;
  addDocuments: (id: number, data: any) => Promise<ApiResponse>;
  callback?: Function;
  permissionCreate?: number;
  isImage: boolean; // initial intent: only images or mixed
  acceptedTypes?: { [key: string]: string[] };
  placeholder?: string;
  module?: string;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  title,
  fieldId,
  open,
  onClose,
  addDocuments,
  callback,
  permissionCreate,
  isImage: initialIsImage = false,
  acceptedTypes,
  placeholder,
  module,
}) => {
  const [acceptedDocuments, setAcceptedDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { displayMessage } = useSnackbar();

  // Standard file type maps
  const defaultImageTypes = { "image/*": [".png", ".gif", ".jpg", ".jpeg"] };
  const commonDocumentTypes = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc", ".docx"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  };

  // Determine accepted types for dropzone
  const currentAcceptedTypes = useMemo(() => {
    if (initialIsImage) {
      // Only images allowed
      return acceptedTypes || defaultImageTypes;
    }
    // Mixed: images + docs + custom
    return {
      ...defaultImageTypes,
      ...commonDocumentTypes,
      ...(acceptedTypes || {}),
    };
  }, [initialIsImage, acceptedTypes]);

  // Convert accepted files to your DTO format and push into state
  const pushAcceptedFiles = useCallback((files: File[]) => {
    const newFiles = files.map((file: File) => ({
      ContentType: file.type,
      FileName: file.name,
      ViewName: file.name.replace(/\.([^.]*)$/, ""),
      Size: file.size,
      File: file,
      isPreviewImage: file.type.startsWith("image/"),
    }));

    setAcceptedDocuments((prevFiles) => [...newFiles, ...prevFiles]);
  }, []);

  // Handle drop with rejections (unaccepted files)
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // 🔴 Unaccepted files
      if (fileRejections.length > 0) {
        const first = fileRejections[0];

        const reason =
          first.errors.map((e) => e.message).join(", ") ||
          t("common.messages.invalidFileType");

        displayMessage(reason, "error");
        // We *can* still process acceptedFiles in the same drop if we want:
        // if (!acceptedFiles.length) return;
        // pushAcceptedFiles(acceptedFiles);
        // For safety, let's continue with accepted ones:
      }

      if (!acceptedFiles.length) return;

      // Extra guard if the dialog is image-only
      if (initialIsImage) {
        const nonImage = acceptedFiles.find(
          (f) => !f.type.startsWith("image/")
        );
        if (nonImage) {
          displayMessage(t("common.messages.invalidFileType"), "error");
          return;
        }
      }

      pushAcceptedFiles(acceptedFiles);
    },
    [displayMessage, pushAcceptedFiles, initialIsImage, t]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: currentAcceptedTypes,
  });

  const handleClose = () => {
    setAcceptedDocuments([]);
    onClose();
  };

  const onSubmit = () => {
    setLoading(true);
    const formData = new FormData();

    // If all accepted docs are images, mark IsImage = true
    const allAreImages = acceptedDocuments.every((item) => item.isPreviewImage);
    formData.append("IsImage", allAreImages ? "true" : "false");

    acceptedDocuments.forEach((item, index) => {
      formData.append(`Files[${index}].file`, item.File);
      formData.append(`Files[${index}].fileName`, item.FileName);
      formData.append(`Files[${index}].viewName`, item.ViewName);
    });

    addDocuments(fieldId, formData)
      .then((resp) => {
        setLoading(false);
        if (resp.status === 200) {
          handleClose();
          displayMessage(
            t("common.apiMsgs.createSuccess", {
              module: module ?? "Document",
            }),
            "success"
          );
          callback && callback();
        } else {
          displayMessage(
            t("common.apiMsgs.createFailed", {
              module: module ?? "Document",
            }),
            "error"
          );
        }
      })
      .catch(() => {
        setLoading(false);
        displayMessage(
          t("common.apiMsgs.createFailed", {
            module: "Document",
          }),
          "error"
        );
      });
  };

  const removeDocument = (item: any) => {
    setAcceptedDocuments((prev) => prev.filter((value) => value !== item));
  };

  const changeViewName = (text: string, index: number) => {
    setAcceptedDocuments((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ViewName: text } : item))
    );
  };

  return (
    <PermissionRestrictedComponent permission={permissionCreate || 0}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            width: "80%",
          },
        }}
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs>
              <Typography
                variant="subtitle1"
                textTransform={"uppercase"}
                fontWeight={"900"}
                color={"primary"}
              >
                {t(title)}
              </Typography>
            </Grid>
            <Grid item>
              <CloseIcon type="button" color="primary" onClick={handleClose} />
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          {/* Dropzone */}
          <Box
            {...getRootProps({ className: "dropzone" })}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 100,
              marginTop: 3,
              border: "2px dashed #000",
            }}
          >
            {!loading && <input {...getInputProps()} />}
            <CloudUpload />
            <Typography variant="body1" sx={{ marginLeft: "10px" }}>
              {placeholder ?? t("common.messages.addFiles")}
            </Typography>
          </Box>

          {/* Preview list */}
          {acceptedDocuments?.length > 0 && (
            <Box py={1}>
              <List
                sx={{
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: 3,
                }}
                component="ul"
              >
                {acceptedDocuments.map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      mb: 1.25,
                      width: "100%",
                      p: 1.25,
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                    }}
                  >
                    <Grid container justifyContent={"space-between"}>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        py={1}
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{ display: "flex" }}
                      >
                        <Divider />
                        {item.isPreviewImage ? (
                          <img
                            src={URL.createObjectURL(item.File)}
                            alt={item.File.name}
                            loading="lazy"
                            style={{
                              width: "100%",
                              maxHeight: 200,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <img
                            src={Document}
                            alt="Document Icon"
                            style={{
                              width: "100%",
                              maxHeight: 200,
                              objectFit: "contain",
                            }}
                          />
                        )}
                      </Grid>

                      <Grid
                        item
                        xs={7}
                        md={6}
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{ display: "flex" }}
                        px={2}
                        py={1}
                      >
                        <TextField
                          value={item.ViewName}
                          rows={1}
                          label={"Document Name"}
                          onChange={(e) => {
                            changeViewName(e.target.value, index);
                          }}
                          fullWidth
                        />
                      </Grid>

                      <Grid
                        item
                        xs={5}
                        md={2}
                        py={1}
                        alignItems={"center"}
                        justifyContent={"center"}
                        sx={{ display: "flex" }}
                      >
                        {!loading && (
                          <Button
                            color="error"
                            variant="contained"
                            onClick={() => removeDocument(item)}
                          >
                            {t("common.buttons.remove")}
                            <DeleteRoundedIcon />
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {acceptedDocuments?.length > 0 &&
            (!loading ? (
              <>
                <Button
                  onClick={handleClose}
                  color="primary"
                  variant="outlined"
                >
                  {t("common.buttons.cancel")}
                </Button>
                <Button color="primary" variant="contained" onClick={onSubmit}>
                  {t("common.buttons.save")}
                </Button>
              </>
            ) : (
              <CustomLoader size={30} />
            ))}
        </DialogActions>
      </Dialog>
    </PermissionRestrictedComponent>
  );
};

export default FileUploadDialog;
